/**
 * A line or two saying what happened to somebody else, over whatever is on screen.
 *
 * "The session continues, and the others are told who left" is the whole requirement, and it has
 * to work in the lobby and in the middle of a firefight. A mode has one error surface and it is a
 * fatal overlay, which is the wrong shape entirely: nothing has gone wrong for the person
 * reading this. So the notices are the shell's, mounted above whatever is showing, and the second
 * mode gets them without knowing they exist.
 *
 * They expire on a timer and they never stack past a few, because a player who spent a minute in a
 * flapping session should be looking at the game rather than at a column of history.
 */

import { el } from '@/ui/dom';
import { injectSessionNoticeStyles } from '@/ui/net/sessionNoticeStyles';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';

/** Long enough to read a short sentence twice, short enough not to sit over a fight. */
const LIFETIME_MS = 5000;

/** Past this, the oldest goes early. A column of history is not something anyone reads. */
const MAX_SHOWN = 3;

export interface SessionNotices {
  show(notice: SessionNotice): void;
  dispose(): void;
}

export function createSessionNotices(container: HTMLElement): SessionNotices {
  const removeStyles = injectSessionNoticeStyles();
  const root = el('div', 'notices');
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');
  container.append(root);

  const timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

  const drop = (line: HTMLElement): void => {
    const timer = timers.get(line);
    if (timer !== undefined) clearTimeout(timer);
    timers.delete(line);
    line.remove();
  };

  return {
    show(notice: SessionNotice): void {
      const line = el('div', 'notice', notice.text);
      root.append(line);
      timers.set(
        line,
        setTimeout(() => {
          drop(line);
        }, LIFETIME_MS),
      );

      while (root.childElementCount > MAX_SHOWN) {
        const oldest = root.firstElementChild;
        if (!(oldest instanceof HTMLElement)) break;
        drop(oldest);
      }
    },

    dispose(): void {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
      root.remove();
      removeStyles();
    },
  };
}
